import { CP_PRODUCT_TYPE, type CpProductType } from '@bcf-vanilla-ts-v1-api-access/config-tokens/env-cp';
import { type CpUserInfoDetails } from '@bcf-vanilla-ts-v1-api-access/cp-api/models/cp-user-info-details';
import { LOG_ROCKET_API_KEY } from '@bcf-vanilla-ts-v1-configurators/tokens/log-rocket/token';
import { type LogRocketWorkerInitializerChannels } from '@bcf-vanilla-ts-v1-logic-worker-others/bloc/log-rocket-worker-initializer/log-rocket-worker-initializer-channels';
import { type TypedSubject } from '@bcf-vanilla-ts-v1-platforms/platform-worker/browser/types';
import {
  type ExtractedObservable,
  type WorkerBlocChannels
} from '@bcf-vanilla-ts-v1-platforms/platform-worker/common/types';
import { provideDiTokenValue } from '@bcf-vanilla-ts-v1-shared/di/di-token';
import { provideSingleton } from '@bcf-vanilla-ts-v1-shared/di/provide-singleton';
import { filterIsTruthy } from '@bcf-vanilla-ts-v1-shared/misc/rx-operators/filter-is-truthy';
import { AppInteraction, provideAppInteraction } from '@bcf-vanilla-ui-ts-v1/shared/app-interaction/app-interaction';
import { type LogRocketFromWebWorkerOptions } from '@bcf-vanilla-ui-ts-v1/shared/log-rocket/types';
import { combineLatest, first, Observable, type SubscriptionLike } from 'rxjs';

export class LogRocketInitializer {
  constructor(
    private _window: Window | undefined,
    private _cpProductType: CpProductType,
    private _appInteraction: AppInteraction,
    private _logRocketApiKey: string
  ) {}

  private _bloc!: UnsubscribableLike<LogRocketWorkerInitializerChannels>;
  private _releaseVersion!: string;

  public init({ langGetter, bloc, releaseVersion }: LogRocketInitOptions): void {
    if (!this._window) {
      return;
    }
    if (!this._logRocketApiKey) {
      return;
    }
    this._bloc = bloc;
    this._releaseVersion = releaseVersion;
    const acceptedCookies$: Observable<unknown> = this._bloc._subTo('analyticsCookiesConsentAccepted');
    this._bloc._sub(
      combineLatest([this._appInteraction.doOnInteract(), acceptedCookies$])
        .pipe(first())
        .subscribe(() => this._setupLogRocket(langGetter))
    );
  }

  private _setupLogRocket(langGetter: () => Promise<string>): void {
    // biome-ignore lint/plugin/typedef: replace eslint/typedef
    import('@bcf-vanilla-ui-ts-v1/shared/log-rocket/log-rocket-loader').then((mod) => {
      // biome-ignore lint/plugin/typedef: replace eslint/typedef
      const instance = mod.provideLogRocketLoader();
      instance.init({
        token: this._logRocketApiKey!,
        langGetter: langGetter,
        releaseVersion: this._releaseVersion,
        origin: this._window!.location.origin,
        productType: this._cpProductType,
        webWorkerFn: this._getWebWorkerFetchAndXhrDataFn()
      });

      this._bloc._sub(
        this._bloc
          ._subTo('userInfoDetails')
          .pipe(filterIsTruthy())
          .subscribe((userInfo: CpUserInfoDetails) => {
            instance.logUser(userInfo);
          })
      );
      this._bloc._workerSubject('doInitializeLogRocket$').next();
    });
  }

  private _getWebWorkerFetchAndXhrDataFn() {
    const unregister = (): void => {
      this._bloc._workerSubject('doRestoreXhrAndFetch$').next();
    };
    const subTo = (
      res: 'Request' | 'Response',
      type: 'Fetch' | 'Xhr',
      options: LogRocketFromWebWorkerOptions
    ): void => {
      this._bloc._sub(
        this._bloc
          ._subTo(`add${res}${type}`)
          .subscribe(([id, response]: [string, any]) =>
            options[`add${res}`].apply(undefined, [
              options[`map${type}WebWorkerIdToId`].apply(undefined, [id]),
              response
            ])
          )
      );
    };
    return (options: LogRocketFromWebWorkerOptions): (() => void) => {
      if (!options.mapFetchWebWorkerIdToId || !options.mapXhrWebWorkerIdToId) {
        console.error('LogRocket from Web Worker Error: missing functions to map id.');
      }
      if (!options.addResponse || !options.addRequest) {
        console.error('LogRocket from Web Worker Error: missing log rockets analytics functions.');
      }

      subTo('Request', 'Xhr', options);
      subTo('Response', 'Xhr', options);
      subTo('Request', 'Fetch', options);
      subTo('Response', 'Fetch', options);

      return unregister;
    };
  }
}
export type LogRocketInitOptions = {
  langGetter: () => Promise<string>;
  bloc: UnsubscribableLike<LogRocketWorkerInitializerChannels>;
  releaseVersion: string;
};

type UnsubscribableLike<R extends WorkerBlocChannels> = {
  _sub(sub: SubscriptionLike | undefined): void;
  _subTo<K extends keyof R['rx'], V extends ExtractedObservable<R['rx'][K]>>(channelRx: K): Observable<V>;
  _workerSubject<V extends TypedSubject<R['subjects'][K]>, K extends keyof R['subjects']>(subjectKey: K): V;
};

export function provideLogRocketInitializer(isServer: boolean): LogRocketInitializer {
  return provideSingleton(LogRocketInitializer, () => {
    const _window: Window | undefined = isServer ? undefined : window;
    return new LogRocketInitializer(
      _window,
      provideDiTokenValue(CP_PRODUCT_TYPE),
      provideAppInteraction(_window, isServer),
      provideDiTokenValue(LOG_ROCKET_API_KEY)
    );
  });
}
